import React, { useEffect, useMemo, useState } from "react";
import { useAlert } from "react-alert";
import {
  AssessmentOutlined,
  ErrorOutline,
  Refresh,
  SchoolOutlined,
} from "@material-ui/icons";
import { useParent } from "../../../../context/ParentContext";
import { baseUrl } from "../../../../services/config";
import "../MonthlyStudentAttendance/parent-progress.css";
import "./exam-results.css";

const getStudentId = (child) =>
  child?.student?.id || child?.student_id || child?.id || null;
const getCampusId = (child) =>
  child?.campus_id || child?.student?.campus_id || child?.campus?.id || null;
const getAdmissionId = (child) =>
  child?.admission_id || child?.student?.admission_id || null;
const getEducationType = (child) =>
  child?.education_type || child?.student?.education_type || 1;

function arrayFrom(result, keys) {
  const payload = result?.payload || result || {};
  if (Array.isArray(payload)) return payload;
  for (const key of keys) {
    if (Array.isArray(payload[key])) return payload[key];
  }
  return [];
}

function examLabel(exam) {
  return exam?.name || exam?.exam_name || exam?.title || `Exam ${exam?.id}`;
}

function normalizeResult(result) {
  const payload = result?.payload || result || {};
  const fullResult = Array.isArray(payload.students_full_result)
    ? payload.students_full_result[0] || {}
    : payload;
  const subjects =
    fullResult.student_result ||
    fullResult.results ||
    fullResult.subjects ||
    payload.student_results ||
    payload.result ||
    [];
  const overall =
    fullResult.student__result_overall ||
    fullResult.student_result_overall ||
    payload.student__result_overall ||
    payload.student_result_overall ||
    {};
  return {
    raw: payload,
    student: fullResult.student || payload.student || {},
    overall: Array.isArray(overall) ? overall[0] || {} : overall,
    subjects: Array.isArray(subjects) ? subjects : [],
  };
}

function numberValue(...values) {
  const value = values.find(
    (item) => item !== undefined && item !== null && item !== "",
  );
  if (value === undefined) return null;
  const parsed = typeof value === "string" ? parseFloat(value) : Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function subjectValues(row) {
  const theoryObtained = numberValue(
    row.gain_marks,
    row.obtained_marks,
    row.marks_obtained,
    row.marks,
  );
  const practical = numberValue(row.practical_marks) || 0;
  const total = numberValue(row.full_marks, row.total_marks, row.max_marks);
  const obtained = theoryObtained === null ? null : theoryObtained + practical;
  const percentage =
    numberValue(row.percentage) ??
    (total && obtained !== null ? (obtained / total) * 100 : null);
  return { obtained, total, percentage };
}

export default function ExamResults() {
  const alert = useAlert();
  const { selectedChild, setUnreadResultCount } = useParent();
  const [sessions, setSessions] = useState([]);
  const [yearId, setYearId] = useState("");
  const [exams, setExams] = useState([]);
  const [examId, setExamId] = useState("");
  const [result, setResult] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [examLoading, setExamLoading] = useState(false);
  const [resultLoading, setResultLoading] = useState(false);
  const [error, setError] = useState("");
  const studentId = getStudentId(selectedChild);
  const campusId = getCampusId(selectedChild);
  const admissionId = getAdmissionId(selectedChild);
  const educationType = getEducationType(selectedChild);
  const studentName = selectedChild?.student?.name || "Student";

  const headers = (json = false) => {
    const token = localStorage.getItem("parentToken");
    return {
      Accept: "application/json",
      ...(json ? { "Content-Type": "application/json" } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const loadSessions = async () => {
    setSessionLoading(true);
    try {
      const response = await fetch(`${baseUrl}/session`, {
        headers: headers(),
      });
      const data = await response.json();
      if (!response.ok)
        throw new Error(data?.message || "Unable to load sessions.");
      const list = Array.isArray(data?.payload) ? data.payload : [];
      setSessions(list);
      const active = list.find(
        (session) => String(session.active_academic_year) === "1",
      );
      setYearId(String(active?.id || list[0]?.id || ""));
    } catch (requestError) {
      const message = requestError.message || "Unable to load sessions.";
      setError(message);
      alert.error(message);
    } finally {
      setSessionLoading(false);
    }
  };

  const loadExams = async () => {
    if (!studentId || !campusId || !yearId) return;
    setExamLoading(true);
    setError("");
    setExams([]);
    setExamId("");
    setResult(null);
    try {
      const fetchSessionExams = async (sessionId) => {
        const response = await fetch(`${baseUrl}/exam_for_session`, {
          method: "POST",
          headers: headers(true),
          body: JSON.stringify({
            year_id: Number(sessionId),
            campus_id: String(campusId),
          }),
        });
        const data = await response.json();
        if (!response.ok)
          throw new Error(data?.message || "Unable to load exams.");
        return arrayFrom(data, ["exams", "exam_list"]);
      };

      let list = await fetchSessionExams(yearId);

      const selectedSession = sessions.find(
        (session) => String(session.id) === String(yearId),
      );
      const isActiveSession =
        String(selectedSession?.active_academic_year) === "1";

      if (!list.length && isActiveSession) {
        for (const session of sessions) {
          if (String(session.id) === String(yearId)) continue;
          const historicalExams = await fetchSessionExams(session.id);
          if (historicalExams.length) {
            list = historicalExams;
            setYearId(String(session.id));
            break;
          }
        }
      }

      if (!list.length) {
        const message = "No exams found for the selected session.";
        setError(message);
        alert.show(message);
        return;
      }
      setExams(list);
      setExamId(String(list[0].id || list[0].exam_id || ""));
    } catch (requestError) {
      const message = requestError.message || "Unable to load exams.";
      setError(message);
      alert.error(message);
    } finally {
      setExamLoading(false);
    }
  };

  const loadResult = async () => {
    if (!admissionId || !campusId || !examId) return;
    setResultLoading(true);
    setError("");
    setResult(null);
    try {
      const response = await fetch(`${baseUrl}/student-result-adm`, {
        method: "POST",
        headers: headers(true),
        body: JSON.stringify({
          exam_id: String(examId),
          admission_id: String(admissionId),
          education_type: String(educationType),
          campus_id: String(campusId),
          include_annual_term: 0,
        }),
      });
      const data = await response.json();
      if (!response.ok)
        throw new Error(data?.message || "Unable to load exam result.");
      const normalized = normalizeResult(data);
      if (!normalized.subjects.length) {
        const message = "No result found for the selected exam.";
        setError(message);
        alert.show(message);
        return;
      }
      setResult(normalized);

      try {
        const markReadResponse = await fetch(
          `${baseUrl}/set_result_notifications?student_id=${encodeURIComponent(
            studentId,
          )}`,
          { headers: headers() },
        );
        if (markReadResponse.ok) setUnreadResultCount(0);
      } catch (markReadError) {
        // Keep the successfully loaded result visible if marking it read fails.
      }
    } catch (requestError) {
      const message = requestError.message || "Unable to load exam result.";
      setError(message);
      alert.error(message);
    } finally {
      setResultLoading(false);
    }
  };

  useEffect(() => {
    loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadExams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, campusId, yearId]);

  useEffect(() => {
    loadResult();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examId]);

  const summary = useMemo(() => {
    if (!result) return { obtained: 0, total: 0, percentage: 0 };
    const calculated = result.subjects.reduce(
      (sum, row) => {
        const values = subjectValues(row);
        return {
          obtained: sum.obtained + Number(values.obtained || 0),
          total: sum.total + Number(values.total || 0),
        };
      },
      { obtained: 0, total: 0 },
    );
    const overallGain = numberValue(
      result.overall.gain_marks,
      result.overall.obtained_marks,
      result.overall.total_gain_marks,
    );
    const obtained =
      overallGain === null
        ? calculated.obtained
        : overallGain +
          Number(numberValue(result.overall.practical_marks) || 0);
    const total =
      numberValue(
        result.overall.full_marks,
        result.overall.total_marks,
        result.overall.total_full_marks,
      ) ?? calculated.total;
    const percentage =
      numberValue(result.overall.percentage) ??
      (total ? (obtained / total) * 100 : 0);
    return { obtained, total, percentage };
  }, [result]);

  const selectedExam = exams.find(
    (exam) => String(exam.id || exam.exam_id) === String(examId),
  );

  return (
    <section className="parent-progress-page parent-exam-page">
      <div className="parent-progress-hero parent-exam-hero">
        <div>
          <span className="parent-progress-eyebrow">Academic performance</span>
          <h1>Exam Results</h1>
          <p>
            {selectedChild
              ? `View subject-wise marks and performance for ${studentName}.`
              : "Select a child to view examination results."}
          </p>
        </div>
        <div className="parent-exam-filters">
          <SelectField
            label="Academic session"
            value={yearId}
            onChange={setYearId}
            loading={sessionLoading}
            placeholder="Select session"
            options={sessions.map((session) => ({
              id: session.id,
              label:
                session.name ||
                session.session_name ||
                session.year ||
                `Session ${session.id}`,
            }))}
          />
          <SelectField
            label="Exam"
            value={examId}
            onChange={setExamId}
            loading={examLoading}
            placeholder="Select exam"
            options={exams.map((exam) => ({
              id: exam.id || exam.exam_id,
              label: examLabel(exam),
            }))}
          />
        </div>
      </div>

      {!studentId ? (
        <ResultState
          icon={SchoolOutlined}
          title="No student selected"
          text="Please select a child from the top menu to view exam results."
        />
      ) : resultLoading || examLoading ? (
        <ResultState
          loading
          title="Loading exam result"
          text="Getting the latest result details."
        />
      ) : error ? (
        <ResultState
          icon={ErrorOutline}
          error
          title="Result unavailable"
          text={error}
          onRetry={examId ? loadResult : loadExams}
        />
      ) : result ? (
        <>
          <div className="parent-exam-summary">
            <Summary label="Exam" value={examLabel(selectedExam)} />
            <Summary
              label="Obtained marks"
              value={summary.obtained}
              accent="blue"
            />
            <Summary label="Total marks" value={summary.total} />
            <Summary
              label="Percentage"
              value={`${summary.percentage.toFixed(1)}%`}
              accent={summary.percentage >= 50 ? "green" : "red"}
            />
          </div>
          <ResultTable subjects={result.subjects} />
        </>
      ) : (
        <ResultState
          icon={AssessmentOutlined}
          title="Select an exam"
          text="Choose an academic session and exam to view the result."
        />
      )}
    </section>
  );
}

function SelectField({
  label,
  value,
  onChange,
  loading,
  placeholder,
  options,
}) {
  return (
    <label>
      <span>{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={loading || !options.length}
      >
        <option value="">{loading ? "Loading..." : placeholder}</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function Summary({ label, value, accent = "" }) {
  return (
    <article className={`parent-exam-summary-card ${accent}`}>
      <span>{label}</span>
      <strong>{value ?? "—"}</strong>
    </article>
  );
}

function ResultTable({ subjects }) {
  return (
    <div className="parent-exam-panel">
      <div className="parent-exam-panel-title">
        <h2>Subject Wise Result</h2>
        <span>{subjects.length} subjects</span>
      </div>
      <div className="parent-exam-table-wrap">
        <table className="parent-exam-table">
          <thead>
            <tr>
              <th>Subject</th>
              <th>Obtained</th>
              <th>Total</th>
              <th>Percentage</th>
              <th>Grade</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {subjects.map((row, index) => {
              const values = subjectValues(row);
              const passed =
                String(row.status || row.result || "").toLowerCase() ===
                  "pass" ||
                (values.percentage !== null && values.percentage >= 40);
              return (
                <tr key={row.id || row.subject_id || index}>
                  <td data-label="Subject">
                    <strong>
                      {row.subject?.name ||
                        row.subject_name ||
                        row.name ||
                        `Subject ${index + 1}`}
                    </strong>
                  </td>
                  <td data-label="Obtained">{values.obtained ?? "—"}</td>
                  <td data-label="Total">{values.total ?? "—"}</td>
                  <td data-label="Percentage">
                    {values.percentage === null
                      ? "—"
                      : `${values.percentage.toFixed(1)}%`}
                  </td>
                  <td data-label="Grade">
                    {row.grade || row.grade_name || "—"}
                  </td>
                  <td data-label="Status">
                    <span
                      className={`parent-exam-status ${
                        passed ? "pass" : "fail"
                      }`}
                    >
                      {row.status ||
                        row.result ||
                        (passed ? "Pass" : "Needs improvement")}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ResultState({ icon: Icon, title, text, loading, error, onRetry }) {
  return (
    <div
      className={`parent-exam-state${error ? " error" : ""}`}
      role={error ? "alert" : "status"}
    >
      {loading ? <span className="parent-exam-spinner" /> : Icon && <Icon />}
      <h2>{title}</h2>
      <p>{text}</p>
      {onRetry && (
        <button type="button" onClick={onRetry}>
          <Refresh /> Try again
        </button>
      )}
    </div>
  );
}
